/**
 * Опрос по экологии клиента для команды Центрики (`/team/clients/:cid/ecology/survey?site=<id>`,
 * ecology-survey): тот же мастер, что у клиента, по API скоупа клиента. Команда видит ответы клиента,
 * может их дополнить и завершить опрос; категорию площадки по ответам эколог ставит в карточке
 * клиента (вкладка «Экология»).
 */
import { useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { teamEcologyApi } from '@/lib/api'
import { useEcoData } from '@/ecology/use-eco-data'
import { useEcoQuestions } from '@/ecology/use-eco-questions'
import { SurveyWizard } from '@/ecology/survey/survey-wizard'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

function ClientEcologySurvey({ cid }: { cid: string }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const api = useMemo(() => teamEcologyApi(cid), [cid])
  const { overview, catalog, loading, error, setOverview } = useEcoData(api)
  const { questions, error: questionsError } = useEcoQuestions()
  const back = () => navigate(`/team/clients/${cid}?tab=ecology`)

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={back}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К карточке клиента
        </Button>
      </div>
      <PageHead
        title="Опрос по экологии"
        subtitle="Ответы клиента по площадке. По ответам кабинет подбирает документы и отчётность"
      />
      {error || questionsError ? (
        <Alert variant="danger">{error ?? questionsError}</Alert>
      ) : loading || !overview || !questions ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : (
        <SurveyWizard
          api={api}
          overview={overview}
          questions={questions}
          catalog={catalog}
          initialSiteId={params.get('site')}
          onOverviewChange={setOverview}
          exit={{
            back: { label: 'Отмена', onClick: back },
            after: [{ label: 'К карточке клиента', onClick: back }],
          }}
        />
      )}
    </div>
  )
}

export function ClientEcologySurveyPage() {
  const { cid } = useParams<{ cid: string }>()
  return cid ? <ClientEcologySurvey cid={cid} /> : null
}
